import * as XLSX from 'xlsx';

/**
 * Exports the currently visible data from an ECharts chart instance into a 2D array.
 * @param {Object} echartsOpt - The ECharts chart instance options.
 * @returns {Array<Array>} A 2D array of the exported data.
 */
export function echartsExportDataSplitGroup(echartsOpt: any): any[][] {
    console.log('Exporting visible data from chart instance:', echartsOpt);

    // if more than one legend throw error
    if (
        echartsOpt.legend &&
        Array.isArray(echartsOpt.legend) &&
        echartsOpt.legend.length > 1
    ) {
        throw new Error('More than one legend is not supported.');
    }

    // if no data there is nothing to export
    if (!echartsOpt.series) {
        return [];
    }

    // Get the selected series from the legend
    const selectedSeries =
        echartsOpt.legend &&
        Array.isArray(echartsOpt.legend) &&
        echartsOpt.legend[0] &&
        echartsOpt.legend[0].selected
            ? echartsOpt.legend[0].selected
            : {};

    // Filter out the series that are not currently visible
    const visibleSeries = echartsOpt.series.filter(
        (series: any) => selectedSeries[series.name] ?? true
    );

    // Prepare data for export using dataset and encode properties
    let dataForExport: any[] = [];
    const header = [echartsOpt.series[0].encode.x].concat(
        visibleSeries.map((series: any) => series.name)
    );
    dataForExport.push(header);

    if (
        echartsOpt.dataset &&
        echartsOpt.dataset[0] &&
        echartsOpt.dataset[0].source
    ) {
        const datasetSource = echartsOpt.dataset[0].source;
        // get distinct and ordered values from the x axis
        const xValues = Array.from(
            new Set(
                // every visible series has the same x axis definition, so we can use the first one
                datasetSource.map((row: any) => row[visibleSeries[0].encode.x])
            )
        );

        // check if all elements of setXValues are numbers then sort as numbers. Else use default sort
        const isNumber = xValues.every((val: any) => typeof val === 'number');
        if (isNumber) {
            xValues.sort((a: any, b: any) => a - b);
        } else {
            xValues.sort();
        }

        let unpivotData = [xValues];
        // for each series
        for (const series of visibleSeries) {
            // const sName = series.name;
            const sMetricName = series.encode.y;

            // if datasetId not in series assign first dataset
            const seriesDataset = series.datasetId
                ? echartsOpt.dataset.find(
                      (dataset: any) => dataset!.id === series.datasetId
                  )
                : echartsOpt.dataset[0];

            // Error handling if dataset is not found
            if (!seriesDataset) {
                throw new Error(`seriesDataset is undefined`);
            }

            // if seriesDataset has source map xValues to yValues
            if (seriesDataset.source) {
                const dSource = seriesDataset.source;
                const dSourceRows = dSource.map((row: any) => {
                    return {
                        x: row[series.encode.x],
                        y: row[sMetricName],
                    };
                });
                unpivotData.push(
                    xValues.map((xValue: any) => {
                        const dSourceRow = dSourceRows.find(
                            (row: any) => row.x === xValue
                        );
                        return dSourceRow ? dSourceRow.y : null;
                    })
                );
            } else {
                // if seriesDataset has transform but no filter throw error
                if (
                    seriesDataset.transform &&
                    !(seriesDataset.transform.type == 'filter')
                ) {
                    throw new Error(
                        'Series dataset have transform but no filter'
                    );
                }

                const dSplitGroupValue = seriesDataset.transform.config.value;

                // map xValues to yValues
                unpivotData.push(
                    xValues.map((xValue: any) => {
                        const dSourceRow = datasetSource.find(
                            (row: any) =>
                                row[series.encode.x] === xValue &&
                                row.split_group === dSplitGroupValue
                        );
                        return dSourceRow ? dSourceRow[sMetricName] : null;
                    })
                );
            }
        }

        // pivot table without using any external functions
        const pivotData = unpivotData[0].map((col: any, i: any) =>
            unpivotData.map((row) => row[i])
        );

        dataForExport = dataForExport.concat(pivotData);
    }
    return dataForExport;
}

/**
 * The options for exporting data.
 * @typedef {Object} DataExportOptions
 * @property {string} filename - The name of the file to be exported.
 */
interface DataExportOptions {
    filename: string;
}

/**
 * Creates and triggers the download of a CSV file from a 2D array of data.
 * @param {Array<Array>} dataForExport - The 2D array of data to be exported.
 * @param {DataExportOptions} options - The options for exporting data.
 */
export function downloadCSV(
    dataForExport: any[][],
    options: DataExportOptions
): void {
    let csvContent = 'data:text/csv;charset=utf-8,';
    dataForExport.forEach((row, index) => {
        if (index === 0) {
            csvContent +=
                row.map((headerCol) => `"${headerCol}"`).join(',') + '\r\n';
        } else {
            csvContent += row.join(',') + '\r\n';
        }
    });
    const encodedUri = encodeURI(csvContent);
    triggerDownload(encodedUri, `${options.filename}.csv`);
}

/**
 * Creates and triggers the download of an XLSX file from a 2D array of data.
 * @param {Array<Array>} dataForExport - The 2D array of data to be exported.
 * @param {DataExportOptions} options - The options for exporting data.
 */
export function downloadXLSX(
    dataForExport: any[][],
    options: DataExportOptions
): void {
    const ws = XLSX.utils.aoa_to_sheet(dataForExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    XLSX.writeFile(wb, `${options.filename}.xlsx`);
}

export function copyToClipboard(
    dataForExport: any[][],
    options?: DataExportOptions
): void {
    navigator.clipboard.writeText(
        dataForExport
            .map((row, index) => {
                if (index === 0) {
                    return row.map((headerCol) => `"${headerCol}"`).join('\t');
                } else {
                    return row.join('\t');
                }
            })
            .join('\n')
    );
}

/**
 * Triggers the download of a file using a data URI.
 * @param {string} uri - The data URI of the file.
 * @param {string} filename - The name of the file to be downloaded.
 */
function triggerDownload(uri: string, filename: string): void {
    const link = document.createElement('a');
    link.setAttribute('href', uri);
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

/**
 * Creates a data exporter function based on specified options.
 * @param {Object} exportOpt - The export options.
 * @param {'csv' | 'xlsx'} exportOpt.type - The file format for export.
 * @param {string} exportOpt.filename - The name of the file to be exported.
 * @param {function} [exportOpt.dataExporter] - The data exporter function to use.
 * @returns {function} A function that exports data from an ECharts instance.
 */
export function dataExporterCreator(exportOpt: {
    /** Export file type */
    type: 'csv' | 'xlsx' | 'clipboard';
    /** Export filename */
    filename: string;
    /** Data exporter function */
    dataExporter: (options: any) => any[][];
}): (echartsOpt: any) => void {
    // const downloadClb = exportOpt.type === 'csv' ? downloadCSV : downloadXLSX;
    const downloadClb = (() => {
        switch (exportOpt.type) {
            case 'csv':
                return downloadCSV;
            case 'xlsx':
                return downloadXLSX;
            case 'clipboard':
                return copyToClipboard;
        }
    })();
    // TODO create type with defaults
    return (options: any) => {
        const dataToExport = exportOpt.dataExporter(options);
        downloadClb(dataToExport, { filename: exportOpt.filename });
    };
}
